/**
 * Official Conceptual Tags used throughout the system
 * These are the ONLY tags used for pattern recognition, neural map connections, and display
 */
export const CONCEPTUAL_TAGS = [
  // Sci-Fi Genres & Movements
  "Cyberpunk",
  "Post-Cyberpunk",
  "Space Opera",
  "Hard Science Fiction",
  "Biopunk",
  "Golden Age",
  
  // Temporal Themes
  "Block Universe Compatible",
  "Time Dilation",
  "Chrono Loops",
  "Technological Shamanism",
  
  // World Structures
  "Utopian Collapse",
  "Mega-Corporate Systems",
  "Off-Earth Civilisations",
  "Dystopian Systems",
  
  // Narrative Form
  "Nonlinear Structure",
  "Dream Logic",
  "Archive-Based",
  "Memory Distortion",
  
  // Sci-Fi Elements
  "Cybernetic Enhancement",
  "Quantum Consciousness",
  "Neural Interface",
  "Posthuman Evolution",

  // Recurring subgenres & themes. Added because a large share of the library
  // carried these exact tags but they weren't recognised, so those books had
  // no conceptual tag at all and sat unconnected on the Neural Map. All appear
  // on 2+ books in the real catalogue.
  "Post-Apocalyptic",
  "First Contact",
  "Science Fantasy",
  "Alternate History",
  "Military SF",
  "Feminist SF",
  "Africanfuturism",
  "New Wave",
  "Dying Earth",
  "Galactic Empire",
  "Telepathy",
  "Immortality",
  "Artificial Intelligence",
  "Survival"
] as const;

/**
 * The same vocabulary, grouped for browsing.
 *
 * The Log Signal form used to render all of these as one flat two-column grid,
 * which is eighteen rows of buttons before you reach the rest of the form. The
 * groups mirror the section comments above so there is one ordering to keep in
 * mind, not two.
 *
 * This is a VIEW of CONCEPTUAL_TAGS, never a second source of truth — every tag
 * must appear in exactly one group, which conceptualTags.test.ts asserts. Add a
 * tag above and the test fails until it is grouped here, so a new node can
 * never quietly become unreachable in the UI.
 */
export const CONCEPTUAL_TAG_GROUPS: ReadonlyArray<{
  label: string;
  tags: readonly string[];
}> = [
  {
    label: "Genres & Movements",
    tags: ["Cyberpunk", "Post-Cyberpunk", "Space Opera", "Hard Science Fiction", "Biopunk", "Golden Age"],
  },
  {
    label: "Temporal Themes",
    tags: ["Block Universe Compatible", "Time Dilation", "Chrono Loops", "Technological Shamanism"],
  },
  {
    label: "World Structures",
    tags: ["Utopian Collapse", "Mega-Corporate Systems", "Off-Earth Civilisations", "Dystopian Systems"],
  },
  {
    label: "Narrative Form",
    tags: ["Nonlinear Structure", "Dream Logic", "Archive-Based", "Memory Distortion"],
  },
  {
    label: "Sci-Fi Elements",
    tags: ["Cybernetic Enhancement", "Quantum Consciousness", "Neural Interface", "Posthuman Evolution"],
  },
  {
    label: "Subgenres & Themes",
    tags: [
      "Post-Apocalyptic", "First Contact", "Science Fantasy", "Alternate History",
      "Military SF", "Feminist SF", "Africanfuturism", "New Wave", "Dying Earth",
      "Galactic Empire", "Telepathy", "Immortality", "Artificial Intelligence", "Survival",
    ],
  },
];

/**
 * Common tag variants → canonical conceptual tag. Keyed lowercase.
 *
 * The single biggest source of unconnected Neural Map nodes: books tagged
 * "Hard SF" (14 of them) never matched "Hard Science Fiction", and "Dystopian"
 * never matched "Dystopian Systems". Normalising these joins them to their
 * cluster without touching any data.
 */
const TAG_SYNONYMS: Record<string, string> = {
  "hard sf": "Hard Science Fiction",
  "hard science-fiction": "Hard Science Fiction",
  "dystopian": "Dystopian Systems",
  "dystopia": "Dystopian Systems",
  "post apocalyptic": "Post-Apocalyptic",
  "post-apocalypse": "Post-Apocalyptic",
  "apocalypse": "Post-Apocalyptic",
  "ai": "Artificial Intelligence",
  "a.i.": "Artificial Intelligence",
  "corporate": "Mega-Corporate Systems",
  "mega-corporate": "Mega-Corporate Systems",
  "posthuman": "Posthuman Evolution",
  "post-human": "Posthuman Evolution",
  "cybernetics": "Cybernetic Enhancement",
};

/** Lowercased index of every canonical tag, so matching is case-insensitive. */
const CANONICAL_BY_LOWER = new Map(
  CONCEPTUAL_TAGS.map((t) => [t.toLowerCase(), t as string])
);

/**
 * Strip emojis from a tag string
 */
const stripEmojis = (str: string): string => {
  return str.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1F018}-\u{1F270}\u{238C}-\u{2454}\u{20D0}-\u{20FF}\u{FE00}-\u{FE0F}]/gu, '').trim();
};

/**
 * Normalise a raw tag list to canonical conceptual tags.
 *
 * Strips emojis, matches case-insensitively, maps known synonyms to their
 * canonical form, and de-duplicates — so "Hard SF" and "Hard Science Fiction"
 * on the same book collapse to one. Anything unrecognised is dropped, as
 * before. Returns canonical names, which is what the map, pattern recognition
 * and the tag chips all display.
 */
export const filterConceptualTags = (tags: string[]): string[] => {
  if (!Array.isArray(tags)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of tags) {
    const cleaned = stripEmojis(String(raw ?? ''));
    if (!cleaned) continue;
    const key = cleaned.toLowerCase();
    const canonical = TAG_SYNONYMS[key] ?? CANONICAL_BY_LOWER.get(key);
    if (canonical && !seen.has(canonical)) {
      seen.add(canonical);
      out.push(canonical);
    }
  }
  return out;
};

/**
 * One-line descriptions for each conceptual tag, used in EntityCard tooltips
 */
export const TAG_DESCRIPTIONS: Record<string, string> = {
  "Cyberpunk": "High tech, low life — advanced technology amid societal decay and corporate control",
  "Post-Cyberpunk": "Beyond cyberpunk's nihilism — technology as tool for social change and hope",
  "Space Opera": "Epic interstellar narratives spanning galaxies, empires and civilisations",
  "Hard Science Fiction": "Rigorous scientific accuracy driving plot, world-building and speculation",
  "Biopunk": "Biotechnology, genetic engineering and organic systems reshaping humanity",
  "Golden Age": "The optimistic era of Asimov, Clarke and Heinlein — big ideas, boundless futures",
  "Block Universe Compatible": "Time as a fixed four-dimensional structure where past, present and future coexist",
  "Time Dilation": "Relativistic time distortion — seconds for travellers, centuries for civilisation",
  "Chrono Loops": "Recursive temporal structures, time loops and paradoxes of causality",
  "Technological Shamanism": "Mystical or spiritual dimensions emerging from advanced technology",
  "Utopian Collapse": "Perfect societies fracturing under the weight of their own ideals",
  "Mega-Corporate Systems": "Corporations as sovereign powers — commerce replacing governance",
  "Off-Earth Civilisations": "Human or alien societies beyond Earth — colonies, stations, generation ships",
  "Dystopian Systems": "Oppressive regimes, surveillance states and controlled societies",
  "Nonlinear Structure": "Fragmented, recursive or multi-threaded narrative architectures",
  "Dream Logic": "Surreal, oneiric storytelling where reality bends to subconscious rules",
  "Archive-Based": "Stories told through documents, records, logs and found materials",
  "Memory Distortion": "Unreliable recall, implanted memories and the fragility of identity",
  "Cybernetic Enhancement": "Human-machine fusion — augmentation, prosthetics and transhumanism",
  "Quantum Consciousness": "Mind and quantum mechanics intertwined — observer effects on reality",
  "Neural Interface": "Direct brain-computer connections — jacking in, mind uploading, digital consciousness",
  "Posthuman Evolution": "Beyond homo sapiens — species transformation through technology or biology",

  // Recurring subgenres & themes
  "Post-Apocalyptic": "Life after collapse — survival amid the ruins of a fallen civilisation",
  "First Contact": "Humanity's first encounter with alien intelligence and the gulf of understanding",
  "Science Fantasy": "The border of SF and fantasy — technology and magic sharing one world",
  "Alternate History": "Divergent timelines where a turn of events reshaped the world",
  "Military SF": "War in the future — soldiers, fleets and the machinery of interstellar conflict",
  "Feminist SF": "Gender, power and society interrogated through a speculative lens",
  "Africanfuturism": "Futures rooted in African cultures, histories and cosmologies",
  "New Wave": "The experimental, literary sixties-seventies turn — inner space over outer",
  "Dying Earth": "The far future of an exhausted world, science faded into myth",
  "Galactic Empire": "Sprawling star-spanning polities — their rise, rule and decline",
  "Telepathy": "Minds touching minds — psychic contact, shared thought and its costs",
  "Immortality": "Deathlessness and radical longevity, and what they do to the self",
  "Artificial Intelligence": "Machine minds — their emergence, rights and reach",
  "Survival": "Endurance against a hostile world, alone or against the odds",
};

/**
 * Author-to-genre fallback mapping for books without tags
 */
export const AUTHOR_GENRE_MAP: Record<string, string> = {
  "Philip K. Dick": "Cyberpunk",
  "William Gibson": "Cyberpunk",
  "Bruce Sterling": "Cyberpunk",
  "Neal Stephenson": "Post-Cyberpunk",
  "George Orwell": "Dystopian Systems",
  "Aldous Huxley": "Dystopian Systems",
  "Margaret Atwood": "Dystopian Systems",
  "Ray Bradbury": "Dystopian Systems",
  "Yevgeny Zamyatin": "Dystopian Systems",
  "Arthur C. Clarke": "Hard Science Fiction",
  "Stanislaw Lem": "Hard Science Fiction",
  "Greg Egan": "Hard Science Fiction",
  "Kim Stanley Robinson": "Hard Science Fiction",
  "Peter Watts": "Hard Science Fiction",
  "Liu Cixin": "Hard Science Fiction",
  "Isaac Asimov": "Golden Age",
  "Robert A. Heinlein": "Golden Age",
  "E.E. Smith": "Golden Age",
  "A.E. van Vogt": "Golden Age",
  "Frank Herbert": "Space Opera",
  "Iain M. Banks": "Space Opera",
  "Dan Simmons": "Space Opera",
  "Joe Haldeman": "Space Opera",
  "Ursula K. Le Guin": "Space Opera",
  "Alastair Reynolds": "Space Opera",
  "Vernor Vinge": "Space Opera",
  "James S.A. Corey": "Space Opera",
  "Larry Niven": "Hard Science Fiction",
  "Octavia E. Butler": "Biopunk",
  "Jeff VanderMeer": "Biopunk",
  "J.G. Ballard": "Dream Logic",
  "Kurt Vonnegut": "Nonlinear Structure",
  "Ted Chiang": "Hard Science Fiction",
  "Cormac McCarthy": "Dystopian Systems",
  "Anthony Burgess": "Dystopian Systems",
  "Philip Eisner": "Hard Science Fiction",
  "Paul R. Ehrlich (influenced by themes)": "Dystopian Systems",
  "Adrian Tchaikovsky": "Hard Science Fiction",
  "Richard K. Morgan": "Cyberpunk",
  "John Scalzi": "Space Opera",
  "Gene Wolfe": "Dream Logic",
  "N.K. Jemisin": "Dystopian Systems",
  "Emily St. John Mandel": "Dystopian Systems",
  "Walter M. Miller Jr.": "Dystopian Systems",
  "Alfred Bester": "Golden Age",
  "Frederik Pohl": "Golden Age",
  "John Wyndham": "Dystopian Systems",
  "Adam Roberts": "Hard Science Fiction",
  "Larry McCaffery": "Cyberpunk",
  "Garfield Benjamin": "Hard Science Fiction",
  "Hazel Monroe": "Hard Science Fiction",
  "Serena Vaughn": "Hard Science Fiction",
  "Gardner R. Dozois": "Space Opera",
  "Jay Williams": "Golden Age",
  "Michael Mortenson": "Hard Science Fiction",
  "Charles Stross": "Space Opera",
  "Pierce Brown": "Space Opera",
  "Mary Shelley": "Gothic Science Fiction",
  "Jules Verne": "Golden Age",
  "H.G. Wells": "Golden Age",
};

/**
 * The conceptual theme implied by an author, used only as a fallback when a
 * book carries no recognised tag of its own.
 *
 * Roughly a quarter of the library has no usable tag, which left those books
 * as unconnected specks on the Neural Map. Inheriting the author's theme gives
 * them somewhere to belong (Ubik → Cyberpunk, Solaris → Hard Science Fiction)
 * without inventing or writing any data — it is derived at render time.
 *
 * Matching ignores diacritics and case, because the same author appears as
 * both "Stanisław Lem" and "Stanislaw Lem" in the catalogue.
 */
const foldName = (s: string) =>
  s
    .normalize('NFD')
    // Strip combining marks (é → e). Handles most accented Latin.
    .replace(/[̀-ͯ]/g, '')
    // Letters that NFD does NOT decompose because they are distinct letters,
    // not accented forms — ł is the one that actually bites here, since the
    // catalogue holds both "Stanisław Lem" and "Stanislaw Lem".
    .replace(/ł/g, 'l').replace(/Ł/g, 'L')
    .replace(/ø/g, 'o').replace(/Ø/g, 'O')
    .replace(/đ/g, 'd').replace(/Đ/g, 'D')
    .replace(/ß/g, 'ss')
    .replace(/æ/g, 'ae').replace(/Æ/g, 'AE')
    .replace(/œ/g, 'oe').replace(/Œ/g, 'OE')
    .toLowerCase()
    .trim();

const GENRE_BY_FOLDED_AUTHOR = new Map(
  Object.entries(AUTHOR_GENRE_MAP).map(([name, genre]) => [foldName(name), genre])
);

export const genreForAuthor = (author?: string | null): string | null => {
  if (!author) return null;
  const genre = GENRE_BY_FOLDED_AUTHOR.get(foldName(author));
  // Only return it if it's a tag the rest of the system recognises, so a
  // stray value here can't create a theme that nothing else understands.
  return genre && CONCEPTUAL_TAGS.includes(genre as never) ? genre : null;
};
